import { useCallback, useEffect, useMemo, useState } from "react";

export type Steps = string[];

export const useProgressBarLoading = ({
  steps,
  initLabel,
}: {
  steps: Steps;
  initLabel: string;
}) => {
  const resolveSteps = useMemo(() => {
    return [initLabel, ...steps];
  }, [steps, initLabel]);
  const [currentValue, setCurrentValue] = useState({
    valueProgress: 0,
    step: initLabel,
    index: 0,
  });
  const [isDone, setIsDone] = useState(false);
  const maxValue = 100;

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    const doneFn = async () => {
      await sleep(1000);
      setIsDone(true);
      return;
    };
    if (currentValue.valueProgress === 100 && !isDone) {
      doneFn().then();
    }
  }, [currentValue, isDone]);

  const nextStep = useCallback(() => {
    setCurrentValue((v) => {
      const newValue = v.valueProgress + 100 / resolveSteps.length;
      const newIndex = v.index + 1;

      return {
        valueProgress: newValue,
        step: resolveSteps[newIndex],
        index: newIndex,
      };
    });
  }, [resolveSteps]);

  const reset = useCallback(() => {
    setCurrentValue({
      valueProgress: 0,
      step: initLabel,
      index: 0,
    });
    setIsDone(false);
  }, [initLabel]);

  const doneAllStep = useCallback(() => {
    setCurrentValue({
      valueProgress: 100,
      step: resolveSteps[resolveSteps.length - 1],
      index: resolveSteps.length - 1,
    });
  }, [resolveSteps]);

  return {
    maxValue,
    currentValue,
    nextStep,
    isDone,
    sleep,
    reset,
    doneAllStep,
  };
};
